import React from "react";
import { Navigate, useLocation, Outlet } from "react-router-dom";
import { TokenService } from "../../services/Api";

/**
 * Guards routes that require authentication.
 * - Checks if a valid token exists
 * - Checks if the logged-in user has an email
 * - Optionally restricts to specific role IDs (e.g. Product Admin = 1)
 *
 * If any check fails → redirects to /login (preserving the attempted URL).
 *
 * Usage:
 *   <Route element={<ProtectedRoute />}>
 *     ...child routes...
 *   </Route>
 *
 *   // Or with role restriction:
 *   <Route element={<ProtectedRoute allowedRoles={[1]} />}>
 *     ...child routes...
 *   </Route>
 */
const ProtectedRoute = ({ allowedRoles = null, children }) => {
  const location = useLocation();

  const token = TokenService.getToken();
  const user = TokenService.getUser();
  const email = user?.email;

  // ✅ Must have a token AND a user object with email
  const isAuthenticated = Boolean(token && email);

  if (!isAuthenticated) {
    // Clean up any stale cookies just in case
    TokenService.clearAll();
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // ✅ Optional role check
  if (
    Array.isArray(allowedRoles) &&
    allowedRoles.length > 0 &&
    !allowedRoles.includes(user?.role?.id)
  ) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // If used as a wrapper around children → render them; otherwise render <Outlet/>
  return children ? children : <Outlet />;
};

export default ProtectedRoute;